import { Component, DestroyRef, computed, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterOutlet } from '@angular/router';
import { CommonModule, DOCUMENT } from '@angular/common';
import {
  T2dAppShellComponent,
  T2dAppUser,
  T2dUserMenuItem,
  T2dToastContainerComponent,
  T2dConfirmDialogHostComponent,
  T2dDeleteDialogHostComponent,
  T2dBrandingService,
} from '@tec2date/ui-components';
import { AuthService } from '../../services/auth.service';
import { InvitationService } from '../../services/invitation.service';
import { ProfileService } from '../../services/profile.service';
import { SchedulerStatusService } from '../../services/scheduler-status.service';
import { MaintenanceBannerComponent } from '../../shared/maintenance-banner/maintenance-banner.component';
import { UpdateAvailableBannerComponent } from '../../shared/update-available-banner/update-available-banner.component';
import { TwoFactorRequiredBannerComponent } from '../../shared/two-factor-required/two-factor-required-banner.component';
import { TwoFactorRequiredModalComponent } from '../../shared/two-factor-required/two-factor-required-modal.component';
import { BreadcrumbComponent } from '../../shared/breadcrumb/breadcrumb.component';
import { T2dEntityTreePickerHostComponent } from '../../shared/entity-lookup/t2d-entity-tree-picker-host.component';
import { DmsDocumentDeleteDialogComponent } from '../../views/dms/components/dms-document-delete-dialog/dms-document-delete-dialog.component';
import { EnvironmentBannerComponent } from '../../shared/environment-banner/environment-banner.component';
import { AppShellPresenceService } from '../../services/app-shell-presence.service';
import { EnvironmentService } from '../../services/environment.service';
import { navItems } from './_nav';
import {
  ICON_USERS,
  ICON_USER,
  ICON_MAIL,
  ICON_SLIDERS,
  ICON_GRID,
  SYSTEM_SETTINGS_ITEMS,
  ORG_SETTINGS_ITEMS,
  toSettingsMenuItems,
} from '../../shared/settings-nav';

@Component({
  selector: 'app-dashboard',
  templateUrl: './default-layout.component.html',
  styleUrls: ['./default-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    T2dAppShellComponent,
    T2dToastContainerComponent,
    RouterOutlet,
    T2dConfirmDialogHostComponent,
    T2dDeleteDialogHostComponent,
    MaintenanceBannerComponent,
    UpdateAvailableBannerComponent,
    TwoFactorRequiredBannerComponent,
    TwoFactorRequiredModalComponent,
    BreadcrumbComponent,
    T2dEntityTreePickerHostComponent,
    DmsDocumentDeleteDialogComponent,
    EnvironmentBannerComponent,
  ],
})
export class DefaultLayoutComponent {
  private readonly authService = inject(AuthService);
  private readonly invitationService = inject(InvitationService);
  private readonly profileService = inject(ProfileService);
  private readonly schedulerStatus = inject(SchedulerStatusService);
  private readonly router = inject(Router);
  private readonly branding = inject(T2dBrandingService);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly shellPresence = inject(AppShellPresenceService);

  /** Badge-Zähler für den „Meine Einladungen"-Eintrag. */
  private readonly pendingInvitationsCount = this.invitationService.myInvitationsCount;

  /** Memberships + aktiver Mandant für den Mandant-Switcher. */
  private readonly hasMultipleMemberships = this.authService.hasMultipleMemberships;
  private readonly activeMemberships = this.authService.activeMemberships;
  private readonly activeMandantId = this.authService.activeMandantId;

  /**
   * Auf Live darf nicht einmal ein leerer Banner-Tag im sidebarExtra-Slot landen:
   * Die UI-Library blendet den Slot per `:empty` aus, und ein Host-Tag ohne Inhalt
   * würde diese Regel aushebeln (8px Leerraum bei Mandanten ohne Logo).
   */
  readonly zeigeUmgebungsBanner = inject(EnvironmentService).kennzeichen !== null;

  /** Mandant-Header-Logo für den Sidebar-Extra-Slot (über dem User-Trigger). */
  readonly sidebarMandantLogoUrl = computed(() => this.authService.activeMembership()?.mandantLogoUrl ?? null);
  readonly sidebarMandantName = computed(() => this.authService.activeMembership()?.mandantName ?? '');

  constructor() {
    // Pending Invitations bei Login einmalig laden (Badge-Quelle); bei Logout leeren.
    effect(() => {
      const user = this.authService.currentUser();
      if (user) {
        this.invitationService.refreshMineQuiet();
      } else {
        this.invitationService.myInvitations.set([]);
      }
    });

    // Mandant-Logo erscheint NICHT mehr im Header — wir rendern es ausschließlich
    // im Sidebar-Bottom-Bereich (sidebarExtra-Slot, siehe HTML-Template). Branding
    // bleibt sicherheitshalber auf reset, falls eine andere Stelle den Service
    // gesetzt hatte.
    effect(() => {
      this.branding.reset();
    });

    // Pending-Invitations-Indikator auf den User-Trigger spiegeln. T2dAppShell
    // zeigt Badges nur im aufgeklappten Popup, nicht am Trigger selbst — der
    // User würde sonst nicht sehen, dass eine offene Einladung existiert.
    // Body-Class + CSS-Var werden in `_custom.scss` zu einem roten Dot mit
    // Zähler oben rechts am Trigger. Sobald die Library den Indikator nativ
    // liefert (siehe Bug-Report 1.7.9-Hotfix), kann dieser Effekt raus.
    effect(() => {
      const count = this.pendingInvitationsCount() + this.schedulerStatus.problemCount();
      const body = this.document.body;
      if (count > 0) {
        body.classList.add('has-pending-user-badges');
        body.style.setProperty('--pending-user-badge-label', `'${count}'`);
      } else {
        body.classList.remove('has-pending-user-badges');
        body.style.removeProperty('--pending-user-badge-label');
      }
    });

    // Scheduler-Problem-Badge: Polling nur für System-Admins (Status-Endpoint ist
    // System-Admin-scoped); bei Logout/Rollenwechsel stoppen + Zähler nullen.
    effect(() => {
      const user = this.authService.currentUser();
      if (user?.isSystemAdmin) {
        this.schedulerStatus.startPolling();
      } else {
        this.schedulerStatus.stopPolling();
      }
    });
    // Beim Logout wird das Layout ZERSTÖRT (Login liegt außerhalb) — der Effect
    // oben feuert dann nicht mehr. Ohne diesen Hook würde der Root-Service
    // unauthentifiziert weiterpollen (catchError schluckt die 401er endlos).
    this.destroyRef.onDestroy(() => this.schedulerStatus.stopPolling());

    // Solange die Sidebar sichtbar ist, zeigt sie das Umgebungs-Banner selbst.
    // Das globale Banner der AppComponent (für Login & Co.) zieht sich dann zurück.
    this.shellPresence.mounted.set(true);
    this.destroyRef.onDestroy(() => this.shellPresence.mounted.set(false));
  }

  /** Sidebar enthält nur noch die produktiven Top-Level-Bereiche. */
  readonly filteredNavItems = computed(() => navItems);

  /**
   * Items für das User-Dropdown der T2dAppShell (Library 1.7.0+).
   *
   * Struktur:
   *   1. „Mandant: <Name>" — Submenu mit allen Memberships (nur ≥2 Memberships)
   *   2. divider
   *   3. „Systemeinstellungen" — Submenu mit Sys-Items (nur SystemAdmin)
   *   4. „Organisationseinstellungen" — Submenu mit Org-Items (MandantAdmin oder SystemAdmin)
   *   5. divider
   *   6. „Mein Profil"
   *   7. „Meine Einladungen" (Badge bei pending)
   *
   * Mandanten-Wechsel via Guard-Route `/mandant-wechsel/:id` — `setActiveMandant`
   * + Redirect zum Dashboard.
   */
  readonly userMenuItems = computed((): T2dUserMenuItem[] => {
    const user = this.authService.currentUser();
    const isSystemAdmin = !!user?.isSystemAdmin;
    const isMandantAdmin = isSystemAdmin || !!user?.rollen.some((r) => r.rollenKey === 'ROLE_MANDANT_ADMIN');

    const items: T2dUserMenuItem[] = [];

    if (this.hasMultipleMemberships()) {
      const activeId = this.activeMandantId();
      const memberships = this.activeMemberships();
      const activeMembership = memberships.find((m) => m.mandantId === activeId);
      const activeName = activeMembership?.mandantName ?? 'Mandant wählen';
      items.push({
        id: 'mandant-switcher',
        label: activeName,
        logoSrc: activeMembership?.mandantMenuLogoUrl ?? null,
        children: memberships.map((m) => ({
          id: `mandant-${m.mandantId}`,
          label: m.mandantName,
          logoSrc: m.mandantMenuLogoUrl ?? null,
          active: m.mandantId === activeId,
          route: `/mandant-wechsel/${m.mandantId}`,
        })),
      });
    }

    if (isSystemAdmin || isMandantAdmin) {
      if (items.length > 0) {
        items.push({ id: 'div-admin', label: '', divider: true });
      }
      if (isSystemAdmin) {
        const schedulerProbleme = this.schedulerStatus.problemCount();
        items.push({
          id: 'system-settings',
          label: 'Systemeinstellungen',
          route: '/systemeinstellungen',
          icon: ICON_SLIDERS,
          ...(schedulerProbleme > 0 ? { badge: schedulerProbleme } : {}),
          children: [
            { id: 'system-settings-overview', label: 'Übersicht', route: '/systemeinstellungen', icon: ICON_GRID },
            ...toSettingsMenuItems(
              SYSTEM_SETTINGS_ITEMS,
              schedulerProbleme > 0 ? { 'sys-scheduler': schedulerProbleme } : undefined,
            ),
          ],
        });
      }
      if (isMandantAdmin) {
        const orgItems = ORG_SETTINGS_ITEMS.filter((it) => !(it.hideForSystemAdmin && isSystemAdmin));
        items.push({
          id: 'org-settings',
          label: 'Organisationseinstellungen',
          route: '/organisationseinstellungen',
          icon: ICON_USERS,
          children: [
            { id: 'org-settings-overview', label: 'Übersicht', route: '/organisationseinstellungen', icon: ICON_GRID },
            ...toSettingsMenuItems(orgItems),
          ],
        });
      }
    }

    items.push({ id: 'div-personal', label: '', divider: true });

    items.push({
      id: 'mein-profil',
      label: 'Mein Profil',
      icon: ICON_USER,
      route: '/profil',
    });

    const pending = this.pendingInvitationsCount();
    items.push({
      id: 'meine-einladungen',
      label: 'Meine Einladungen',
      icon: ICON_MAIL,
      route: '/meine-einladungen',
      ...(pending > 0 ? { badge: pending } : {}),
    });

    return items;
  });

  readonly appUser = computed((): T2dAppUser | null => {
    const user = this.authService.currentUser();
    if (!user) return null;
    // Avatar-URL nur setzen, wenn Backend ein Bild hat. Sonst undefined lassen
    // → T2dAppShell rendert automatisch den Initialen-Fallback.
    const avatarUrl = user.hatProfilbild
      ? (this.profileService.getPictureUrl(user.profilbildAktualisiertAm) ?? undefined)
      : undefined;
    return {
      name: `${user.vorname} ${user.nachname}`.trim(),
      role: user.rollen?.[0]?.name ?? 'Benutzer',
      avatarUrl,
    };
  });

  onLogout(): void {
    this.authService
      .logout()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.router.navigate(['/login']),
        error: () => this.router.navigate(['/login']),
      });
  }
}
